import { Component, effect, inject, input, model, OnDestroy, OnInit, signal } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { MatterDto } from '@shared/interfaces/dtos/matters';
import { PointStatusDto, PointTypeDto } from '@shared/interfaces/dtos/points';
import { ServiceDto } from '@shared/interfaces/dtos/services';
import { MyRightsForPoints } from '@shared/models/rights';
import { MattersService } from '@shared/services/matters.service';
import { PointsService } from '@shared/services/points.service';
import { ServicesService } from '@shared/services/services.service';
import { GeneralInformationsFormGroup } from '@shared/types/points';
import { ServiceTreeUtils } from '@shared/utils/sercicesTree.utils';
import { TreeNode } from 'primeng/api';
import { TreeNodeSelectEvent } from 'primeng/tree';
import { finalize, map } from 'rxjs';
import { PointsAdvancedSearchService } from '../services/points-advanced-search.service';
import { extractElementsFromQueryParam } from '@shared/helpers';

@Component({
  selector: 'idel-general-data',
  templateUrl: './general-data.component.html',
  styleUrl: './general-data.component.scss',
})
export class GeneralDataComponent implements OnInit, OnDestroy {
  private readonly pointsAdvancedSearchService = inject(PointsAdvancedSearchService);
  private readonly service = inject(PointsService);
  private readonly servicesService = inject(ServicesService);
  private readonly mattersService = inject(MattersService);

  public readonly form = model.required<FormGroup<GeneralInformationsFormGroup>>();

  public readonly rights = input.required<MyRightsForPoints>();

  public readonly fetchingPointTypes = signal<boolean>(false);
  public readonly fetchingPointStatuses = signal<boolean>(false);
  public readonly fetchingServices = signal<boolean>(false);
  public readonly fetchingMatters = signal<boolean>(false);

  public readonly pointTypesOptions = signal<PointTypeDto[]>([]);
  public readonly pointStatusesOptions = signal<PointStatusDto[]>([]);
  public readonly servicesOptions = signal<TreeNode<ServiceDto>[]>([]);
  public readonly mattersOptions = signal<MatterDto[]>([]);

  private readonly shouldPopulate = this.pointsAdvancedSearchService.shouldPopulateForms;
  private readonly shouldPopulateEff = effect(
    () => {
      const shouldPopulate = this.shouldPopulate();

      if (shouldPopulate) {
        this.populateFromCurrentSearch();
      }
    },
    { allowSignalWrites: true }
  );

  ngOnInit(): void {
    this.initOptions();
  }

  ngOnDestroy(): void {
    this.shouldPopulateEff.destroy();
  }

  private populateFromCurrentSearch(): void {
    const currentSearch = this.pointsAdvancedSearchService.currentSearch();

    if (!currentSearch) return;

    this.pointsAdvancedSearchService.setFormPopulationProgressAsStartedFor('generalData');

    const selectedPointTypes = this.pointTypesOptions().filter((type) => {
      const typeIds = extractElementsFromQueryParam(currentSearch?.TypeIds);

      if (!typeIds.length) return false;

      return typeIds.includes(type.id.toString());
    });
    const selectedPointStatuses = this.pointStatusesOptions().filter((status) => {
      const statusIds = extractElementsFromQueryParam(currentSearch?.StatutIds);

      if (!statusIds.length) return false;

      return statusIds.includes(status.id.toString());
    });
    const selectedServices = this.servicesOptions().filter((service) => {
      const serviceIds = extractElementsFromQueryParam(currentSearch?.ServiceIds);

      if (!serviceIds.length) return false;

      return serviceIds.includes(service.data.id.toString());
    });

    const selectedMatters = this.mattersOptions().filter((matter) => {
      const matterIds = extractElementsFromQueryParam(currentSearch?.MatiereIds);
      if (!matterIds.length) return false;
      return matterIds.includes(matter.id.toString());
    });

    this.form().setValue({
      excludeNotIncludedPoints: currentSearch.ExclureNonInclus ?? null,
      includeCancelPoints: currentSearch.InclureAnnuler ?? null,
      myPointsToCorrect: currentSearch.ACorriger ?? null,
      searchIntoContent: currentSearch.CheckContenuRTF ?? null,
      onlyUnreads: currentSearch.NeverReadOnly ?? null,
      keyword: currentSearch.RechercheGlobale ?? null,
      pointTypes: selectedPointTypes ?? [],
      pointStatuses: selectedPointStatuses ?? [],
      services: selectedServices ?? [],
      matters: selectedMatters ?? [],
    });

    this.pointsAdvancedSearchService.setFormPopulationProgressAsDoneFor('generalData');
  }

  private initOptions(): void {
    this.fetchPointTypes();
    this.fetchPointStatuses();
    this.fetchServices();
    this.fetchMatters();
  }

  private fetchPointTypes(): void {
    this.fetchingPointTypes.set(true);

    this.service
      .fetchPointTypes()
      .pipe(finalize(() => this.fetchingPointTypes.set(false)))
      .subscribe({
        next: (types) => {
          const allowedTypes = types.filter((type) => this.rights().allowedPointTypeIdsToView().includes(type.id));
          this.pointTypesOptions.set(allowedTypes);
        },
      });
  }

  private fetchPointStatuses(): void {
    this.fetchingPointStatuses.set(true);

    this.service
      .fetchPointStatuses()
      .pipe(finalize(() => this.fetchingPointStatuses.set(false)))
      .subscribe({
        next: (statuses) => {
          const allowedStatuses = statuses.filter((status) =>
            this.rights().allowedStatusesIdsToView().includes(status.id)
          );
          this.pointStatusesOptions.set(allowedStatuses);
        },
      });
  }

  private fetchServices(): void {
    this.fetchingServices.set(true);

    this.servicesService
      .fetchServices()
      .pipe(
        finalize(() => this.fetchingServices.set(false)),
        map((res) => ServiceTreeUtils.toTreeNodes(res))
      )
      .subscribe({
        next: (services) => {
          this.servicesOptions.set(services);
        },
      });
  }

  private fetchMatters(): void {
    this.fetchingMatters.set(true);

    this.mattersService
      .fetchMatters()
      .pipe(finalize(() => this.fetchingMatters.set(false)))
      .subscribe({
        next: (matters) => {
          const allowedMatters = matters.filter((matter) =>
            this.rights().allowedMattersIdsToView().includes(matter.id)
          );
          this.mattersOptions.set(allowedMatters);
        },
      });
  }

  public selectAllServices(): void {
    this.form().controls.services.setValue(this.servicesOptions());
  }
}
